import { useState, useEffect } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import { OverlayTrigger } from 'react-bootstrap';
import { StyledPopover } from '../reports/debt_list/debtList.styled';
import { StyledPopoverBody } from '../reports/expensesMonthlyNew2/MonthlyExpensesNew.styled';
import BllinkUtils from '../../common/utils/utils';
import { CurrencyValue } from '../../common/currencyValue';
import { constants } from '../../common/constants/constants';
import { calcMonthsTextForSelectBox, shortenText } from '../../common/shared/statelessMethods';
import logo from '../../images/newDesign/bllink_logo.svg';
import RefundModal from '../reports/apartment_cashflow_table/RefundModal';
import { StopPlansModal } from '../reports/yearlyByMonths/StopPlansModal';
import { StyledCard } from './CollectionStatus.styled';
import {hideLoader, showLoader} from "../../store/common/slice";
import ErrorsHandler from "../../common/errors/handleErrors";

const TimeUtils = BllinkUtils.BllinkTime;


/**
 * @param {DepositedPayment} record
 * @param {ServerData} client
 * */
const DepositCard = ({ record, bllinkCharges, csvCallback, buildingID, client, onPaymentStop, isBankDeposit }) => {
    const { t } = useTranslation();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const dispatch = useDispatch();

    const [showPaymentInfo, setShowPaymentInfo] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [stopPlanContext, setStopPlanContext] = useState({ show: false });
    const [paymentInfo, setPaymentInfo] = useState(null);

    const depositDate = TimeUtils.parseDate(record.depositDate);
    const chargeDate = TimeUtils.parseDate(record?.chargeDate);
    const type = t(`buildings.${constants.APIContract.paymentMethodsById[record.paymentType].serverKey}`);
    const statusKey = constants.PaymentPlanDetailsStatus[record.paymentState]; 

    // deposit status
    const status = record.depositState == null ?  '' : t(`statuses.${record.depositState}`);

    const paymentComment = () => {
        let comment = '';
        if (record.months && record.months.length > 0) comment = shortenText(calcMonthsTextForSelectBox(record.months));
        else if (record.oneTime) comment = shortenText(record.oneTime.PaymentSettings.Description.description);
        return comment.shortComment;
    };

    async function openCloseDetails() {
        if (!showPaymentInfo) {
            if (paymentInfo == null) {
                dispatch(showLoader());
                try {
                    const info = await client.managers.payments.getPaymentInfo(buildingID, record.paymentId);
                    setPaymentInfo(info);
                    setShowPaymentInfo(true);
                } catch (e) {
                    ErrorsHandler.notifyClientError(e);
                } finally {
                    dispatch(hideLoader());
                }
            } else {
                setShowPaymentInfo(true);
            }
        } else {
            setShowPaymentInfo(false);
        }
    }

    const redirectToConfirmationPage = () => {
        window.open(`/status/${record.cartID}`, '_blank');
        setShowPopover(false);
    };

    useEffect(() => csvCallback({
        apartmentNum: record.apartmentNum,
        tenantName: record.tenantName,
        depositDate: depositDate,
        type: type,
        status: status,
        paidFor: paymentComment(),
        chargeDate: bllinkCharges ? chargeDate : '',
        amount: record.amount
    }), [record]);

    const onRefundClick = () => setShowRefundModal(true);
    const onRefundModalHide = () => setShowRefundModal(false);

    const handleStopOnlineClick = () => {
        const paymentPlanId = record.PaymentPlanId;
        setStopPlanContext({ show: true, buildingID, record: { apartmentNumber: record.apartmentNum, paymentPlanId  }, year: 'all', paymentPlanId })
    };

    const handleStopOnlineHide = (stopped, record) => {
        setStopPlanContext({ show: false });
        if (stopped) onPaymentStop(record);
    };

    const onUpdateCreditCard = () => window.open(`${constants.frontendUrl}/${constants.failedPaymentPage}/${record.cartIdForFailed}`, '_blank');

    /**
     * @param {PaymentInfo} info
     * */
    function PaymentInfo({ info, isBankDeposit }) {
        return (
            <div className='payment-info'>
                <div></div>
                <div></div>
                {isBankDeposit && (
                    <>
                        <div></div>
                        <div></div>
                        <div></div>                  
                    </>
                )}
                <div className='info-list'>
                    {info?.creditCardDetails?.cardType || info?.creditCardDetails?.lastNums || info?.creditCardDetails?.expireDate 
                    ? (<>
                        <div>{t('reports.card_type')} <span className='highlited-info'>{info.creditCardDetails.cardType}</span></div>
                        <div>{t('payments.4_last_digits')} <span className='highlited-info'>{info.creditCardDetails.lastNums}</span></div>
                        <div>{t('reports.expire_date')} <span className='highlited-info'>{info.creditCardDetails.expireDate}</span></div>
                    </>) 
                    : info.creditCardDetails != null ? <div>{t('reports.noDataAvailable')}</div> : <></>}
                    {info?.bankChargeDetails?.bankCode || info?.bankChargeDetails?.branchId || info?.bankChargeDetails?.accountId != null 
                    ? (<>
                        <div>{t('reports.bank_code')} <span className='highlited-info'>{info.bankChargeDetails.bankCode}</span></div>
                        <div>{t('expenses.branch')} <span className='highlited-info'>{info.bankChargeDetails.branchId}</span></div>
                        <div>{t('reports.account')} <span className='highlited-info'>{info.bankChargeDetails.accountId}</span></div>
                    </>) 
                    : info?.bankChargeDetails != null ? <div>{t('reports.noDataAvailable')}</div> : <></>}
                    {info?.wireDetails?.bankCode || info?.wireDetails?.branchId || info?.wireDetails?.accountId || info?.wireDetails?.wireConfirmationNum? (<>
                        <div>{t('reports.bank_code')} <span className='highlited-info'>{info.wireDetails.bankCode}</span></div>
                        <div>{t('expenses.branch')} <span className='highlited-info'>{info.wireDetails.branchId}</span></div>
                        <div>{t('reports.account')} <span className='highlited-info'>{info.wireDetails.accountId}</span></div>
                        <div>{t('expenses.bank_adjustments.wire_number')} <span className='highlited-info'>{info.wireDetails.wireConfirmationNum}</span></div>
                    </>) 
                    : info.wireDetails != null ? <div>{t('reports.noDataAvailable')}</div> : <></>}
                    {info?.chequeDetails?.bankCode || info?.chequeDetails?.branchId || info?.chequeDetails?.accountId || info?.chequeDetails?.chequeNum ? (<>
                        <div>{t('reports.bank_code')} <span className='highlited-info'>{info.chequeDetails.bankCode}</span></div>
                        <div>{t('expenses.branch')} <span className='highlited-info'>{info.chequeDetails.branchId}</span></div>
                        <div>{t('reports.account')} <span className='highlited-info'>{info.chequeDetails.accountId}</span></div>
                        <div>{t('expenses.cheque_num')} <span className='highlited-info'>{info.chequeDetails.chequeNum}</span></div>
                    </>) 
                    : info.chequeDetails != null ? <div>{t('reports.noDataAvailable')}</div> : <></>}
                </div>
                <div></div>
            </div>
        );
    }

    return (
        <StyledCard ltr={ltr} showPaymentInfo={showPaymentInfo} bllinkCharges={bllinkCharges}>
            {
                bllinkCharges ? (
                    <div className='body-card'>
                        <div>{chargeDate}</div>
                        <div>{depositDate}</div>
                        <div className='payment-type' onClick={openCloseDetails}>
                            <span className='inside-type'>{type}{!record.isOnline ? <img src={logo} /> : ''}</span>
                        </div> 
                        <div>{record.apartmentNum}</div>
                        <div className='tenant'>{record.tenantName}</div>
                        <CurrencyValue currency={record.amount.currency}>{record.amount.value}</CurrencyValue>
                        <div>{paymentComment()}</div>
                        <div>{status}</div>
                    </div>
                ) : (
                    <div className='body-card deposit'>
                        <div>{depositDate}</div>
                        <CurrencyValue currency={record.amount.currency}>{record.amount.value}</CurrencyValue>
                        <div>{record.apartmentNum}</div>
                        <div className='tenant'>{record.tenantName}</div>
                        <div>{paymentComment()}</div>
                        <div className='payment-type' onClick={openCloseDetails}>
                            <span className='inside-type'>{type}{record.isOnline ? <img src={logo} /> : ''}</span>
                        </div> 
                        <div>{status}</div>
                    </div>
                )
            }
            
            <OverlayTrigger
                rootClose
                show={showPopover}
                onToggle={() => setShowPopover(!showPopover)}
                trigger="click"
                placement="bottom-start"
                overlay={(
                    <StyledPopover>
                        <StyledPopoverBody>
                            <div className='link' onClick={redirectToConfirmationPage}>{t('reports.collection_open_confirmation')}</div>
                            {bllinkCharges && (
                                <>
                                    <div className={`link ${record.isRefundable ? '' : 'disabled'}`} onClick={onRefundClick}>{t('reports.collection_refund')}</div>
                                    <div className={`link ${statusKey == 9 ? '' : 'disabled'}`} onClick={onUpdateCreditCard}>{t('reports.update_credit_card')}</div>
                                    <div className={`link ${record.isStoppable ? '' : 'disabled'}`} onClick={handleStopOnlineClick}>{t('reports.collection_cancel')}</div>                              
                                </>
                            )}
                        </StyledPopoverBody>
                    </StyledPopover>
                )
            }>
                <div className='tool'>
                    <div className='dot'></div>
                    <div className='dot'></div>
                    <div className='dot'></div>
                </div>
            </OverlayTrigger>

            {showPaymentInfo && (<PaymentInfo info={paymentInfo} isBankDeposit={isBankDeposit} />)}

            <RefundModal
                show={showRefundModal}
                onHide={onRefundModalHide}
                paymentID={record.paymentId}
                buildingID={buildingID}
                client={client}
            />

            <StopPlansModal onHide={handleStopOnlineHide} {...stopPlanContext} />
        </StyledCard>
    )
}

export default DepositCard;

